import type { ComponentProps, ReactNode } from 'react';
import { createContext } from 'react';
import { BrowserRouter, HashRouter } from 'react-router-dom';

import { FlagProvider } from '@fusion/feature-flags';
import { MetricsProvider } from '@cognite/telemetry';
import type { QueryClient } from '@tanstack/react-query';

import { AiProvider } from '@cognite/ai/provider';
import {
  AuthConsumer,
  AuthProvider,
  hasOfflineAuthSession,
  OfflineAuthProvider,
} from '@cognite/e2e-auth';

import { isProduction } from '../features/auth/utils';
import { getRouterBasenameFromPathname } from '../features/routing/project-path';
import { useInfieldLanguage } from '../features/i18n/use-infield-language';
import { registerInfieldLanguageInMetrics } from '../features/metrics/infield-language';
import { instrumentation } from '../utils/instrumentation';

import { AppProviderSwitcher } from './app-providers-switcher';
import { DeviceProvider } from './device-provider/device-provider';
import { createInstrumentedQueryClient } from './query-client';

// PR previews in github pages are nested, so require the HashRouter.
const isPRPreview = window.location.host === 'cognitedata.github.io';

const getRouterBasename = (): string | undefined => {
  if (isPRPreview) {
    return undefined;
  }

  return getRouterBasenameFromPathname(
    window.location.pathname,
    window.location.hostname
  );
};

type UseInfieldLanguage = Pick<
  ReturnType<typeof useInfieldLanguage>,
  'selectedLanguage'
>;

export interface AppProvidersContextInterface {
  Router: typeof HashRouter;
  AppProviderSwitcher: typeof AppProviderSwitcher;
  AiProvider: typeof AiProvider;
  AuthProvider: typeof AuthProvider;
  OfflineAuthProvider: typeof OfflineAuthProvider;
  AuthConsumer: (props: ComponentProps<typeof AuthConsumer>) => ReactNode;
  hasOfflineAuthSession: typeof hasOfflineAuthSession;
  MetricsProvider: typeof MetricsProvider;
  FlagProvider: typeof FlagProvider;
  queryClient: QueryClient;
  isProduction: () => boolean;
  getRouterBasename: () => string | undefined;
  DeviceProvider: typeof DeviceProvider;
  useInfieldLanguage: () => UseInfieldLanguage;
  registerInfieldLanguageInMetrics: typeof registerInfieldLanguageInMetrics;
}

const createQueryClient = createInstrumentedQueryClient({
  trackSLI: instrumentation.trackSLI.bind(instrumentation),
  trackEvent: instrumentation.trackEvent.bind(instrumentation),
});

export const defaultValue: AppProvidersContextInterface = {
  Router: isPRPreview ? HashRouter : BrowserRouter,
  AppProviderSwitcher,
  AiProvider,
  AuthProvider,
  OfflineAuthProvider,
  AuthConsumer,
  hasOfflineAuthSession,
  MetricsProvider,
  FlagProvider,
  queryClient: createQueryClient(),
  isProduction,
  getRouterBasename,
  DeviceProvider,
  useInfieldLanguage,
  registerInfieldLanguageInMetrics,
};

export const AppProvidersContext =
  createContext<AppProvidersContextInterface>(defaultValue);
